/**
 * Campos de cadastro padronizados (portados da DEMO-03, CamposCadastro.tsx):
 * CNPJ e CEP aceitos com ou sem máscara e formatados automaticamente; UF em
 * lista (27 unidades); Origem em lista padronizada com "Quem indicou?".
 * As regras de validação são as MESMAS do servidor (compartilhado/documentos.ts).
 * Duplicidade de CNPJ: consultada na API (CNPJ único em todo o HUB).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { api, ErroApi } from '../api.ts';
import { soDigitos, UFS, validarCep, validarCnpj } from '../../../backend/src/compartilhado/documentos.ts';
import { Ajuda } from './ui.tsx';
import { rotuloTipoIndicacao, textoIndicacao, type ClienteResumo, type ContatoConsolidado, type Indicacao } from './tipos.ts';

export { textoIndicacao };

interface Existente {
  cadastrado: boolean;
  tipo?: string;
  nome?: string;
  razao_social?: string;
  rota?: string;
}

/** Onde o CNPJ já está cadastrado (consulta à API quando o CNPJ fica válido). */
export function useCnpjExistente(cnpj: string, valido: boolean): Existente | null {
  const [x, setX] = useState<Existente | null>(null);
  useEffect(() => {
    setX(null);
    if (!valido) return;
    let vivo = true;
    const t = window.setTimeout(() => {
      api
        .get<Existente>(`/api/clientes/cnpj/${soDigitos(cnpj)}`)
        .then((r) => vivo && setX(r))
        .catch(() => undefined);
    }, 250);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [cnpj, valido]);
  return x;
}

const AJUDA_CNPJ = 'Digite com ou sem máscara: a formatação é automática e os dígitos verificadores são conferidos.';
const AJUDA_CEP = 'Com ou sem hífen: a formatação é automática.';

/** F1-41: orientação dos campos de Cliente com consulta automática (texto que ficava fixo no topo do Novo cliente). */
export const AJUDA_CNPJ_CONSULTA = `${AJUDA_CNPJ} Ao completar um CNPJ válido, o HUB consulta a Receita Federal (BrasilAPI) e sugere razão social, nome fantasia, situação cadastral, matriz/filial e endereço — tudo editável; a inscrição estadual é sempre manual. O CNPJ é conferido contra duplicidade em todo o HUB.`;
export const AJUDA_CEP_CONSULTA = `${AJUDA_CEP} Ao completar o CEP, o HUB consulta o endereço (ViaCEP, com BrasilAPI de reserva) e sugere Logradouro, Bairro, Município e UF — confira e corrija se precisar; o Número é sempre manual.`;

export function CampoCnpj({ rotulo = 'CNPJ', valor, aoMudar, teste, obrigatorio, aceitarFicticio, ignorarRota, ajuda = AJUDA_CNPJ }: { rotulo?: string; valor: string; aoMudar: (v: string, valido: boolean) => void; teste?: string; obrigatorio?: boolean; aceitarFicticio: boolean; ignorarRota?: string; ajuda?: string }) {
  const id = useId();
  const [tocado, setTocado] = useState(false);
  const r = validarCnpj(valor, { aceitarFicticio });
  const existente = useCnpjExistente(r.ok ? r.formatado : '', r.ok);
  const duplicado = existente?.cadastrado && existente.rota !== ignorarRota ? existente : null;
  return (
    <div className="campo-cnpj">
      <label htmlFor={id}>
        {rotulo}
        {obrigatorio && <span aria-hidden> *</span>}
        {/* F1-41: sem texto fixo abaixo do campo; a orientação fica na ajuda "?" */}
        <Ajuda titulo={rotulo}>{ajuda}</Ajuda>
      </label>
      <input
        id={id}
        value={valor}
        inputMode="numeric"
        placeholder="00.000.000/0000-00 ou só os números"
        aria-invalid={tocado && !r.ok}
        aria-describedby={`${id}-msg`}
        onChange={(e) => {
          const x = validarCnpj(e.target.value, { aceitarFicticio });
          aoMudar(x.ok ? x.formatado : e.target.value, x.ok);
        }}
        onBlur={() => setTocado(true)}
        data-teste={teste}
      />
      <small id={`${id}-msg`} className={tocado && !r.ok ? 'erro-campo' : 'ajuda'} role={tocado && !r.ok ? 'alert' : undefined} data-teste={teste ? `${teste}-msg` : undefined}>
        {!valor.trim() ? '' : !r.ok ? (tocado ? r.erro : `${soDigitos(valor).length} de 14 dígitos`) : r.ficticio ? 'CNPJ fictício (faixa reservada, sem empresa real).' : 'CNPJ válido (dígitos verificadores conferidos).'}
      </small>
      {duplicado && (
        <p className="aviso aviso-alerta" role="alert" data-teste={teste ? `${teste}-duplicado` : undefined}>
          <span className="rotulo-nivel">▲ Atenção</span>Este CNPJ já está cadastrado ({duplicado.tipo === 'cliente' ? 'Cliente' : 'Parceiro comercial'} <strong>{duplicado.nome}</strong> — {duplicado.razao_social}). Não cadastre de novo:{' '}
          {duplicado.rota ? <a href={`#${duplicado.rota}`}>abrir o cadastro existente</a> : 'procure o cadastro existente'}.
        </p>
      )}
    </div>
  );
}

export function CampoCep({ valor, aoMudar, teste, rotulo = 'CEP', ajuda = AJUDA_CEP }: { valor: string; aoMudar: (v: string) => void; teste?: string; rotulo?: string; ajuda?: string }) {
  const id = useId();
  const [tocado, setTocado] = useState(false);
  const r = validarCep(valor);
  return (
    <div className="campo-cep">
      <label htmlFor={id}>
        {rotulo}
        <Ajuda titulo={rotulo}>{ajuda}</Ajuda>
      </label>
      <input
        id={id}
        value={valor}
        inputMode="numeric"
        placeholder="00000-000 ou 8 números"
        aria-invalid={tocado && Boolean(valor) && !r.ok}
        aria-describedby={`${id}-msg`}
        onChange={(e) => {
          const x = validarCep(e.target.value);
          aoMudar(x.ok ? x.formatado : e.target.value);
        }}
        onBlur={() => setTocado(true)}
        data-teste={teste}
      />
      <small id={`${id}-msg`} className={tocado && valor && !r.ok ? 'erro-campo' : 'ajuda'}>
        {tocado && valor && !r.ok ? r.erro : ''}
      </small>
    </div>
  );
}

/** Endereço sugerido pela consulta de CEP (o que a tela preenche: Logradouro, Bairro, Município e UF). */
export interface EnderecoCep {
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}
type RespostaCep = { encontrado: true; dados: EnderecoCep; nomeFonte: string; consultadoEm: string } | { encontrado: false; motivo: string; mensagem: string };
export type SituacaoConsultaCep = { tipo: 'consultando' | 'encontrado' | 'falhou'; texto: string } | null;
const MENSAGEM_FALHA_CEP = 'Não foi possível consultar o CEP. Preencha os dados manualmente.';

/**
 * Consulta AUTOMÁTICA de CEP (decisão da Valéria, 07/10/2026): só quando o CEP fica completo (8 dígitos válidos) e
 * DIFERENTE do último consultado — nunca a cada tecla, nunca repetindo o mesmo CEP, nunca ao abrir um cadastro com o
 * CEP já salvo. O resultado é sugestão: preenche os campos (que continuam editáveis) e mostra a fonte e a hora; a
 * falha só informa e o preenchimento manual segue normalmente. Nada é gravado pela consulta.
 */
export function useConsultaCep(cep: string, aoEncontrar: (e: EnderecoCep) => void): { situacao: SituacaoConsultaCep; marcarConsultado: (cep: string) => void } {
  const salvo = soDigitos(cep);
  const ultimo = useRef(salvo.length === 8 ? salvo : '');
  const callback = useRef(aoEncontrar);
  callback.current = aoEncontrar;
  const [situacao, setSituacao] = useState<SituacaoConsultaCep>(null);
  useEffect(() => {
    const d = soDigitos(cep);
    if (d.length !== 8 || !validarCep(cep).ok) {
      if (d !== ultimo.current) setSituacao(null);
      return;
    }
    if (d === ultimo.current) return;
    ultimo.current = d;
    let vigente = true;
    let terminou = false;
    setSituacao({ tipo: 'consultando', texto: 'Consultando o CEP…' });
    api
      .get<RespostaCep>(`/api/clientes/cep/${d}`)
      .then((r) => {
        terminou = true;
        if (!vigente) return;
        if (r.encontrado) {
          callback.current({ logradouro: r.dados.logradouro, bairro: r.dados.bairro, cidade: r.dados.cidade, uf: r.dados.uf });
          const quando = new Date(r.consultadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
          setSituacao({ tipo: 'encontrado', texto: `Dados consultados via ${r.nomeFonte} em ${quando} — confira antes de continuar.` });
        } else setSituacao({ tipo: 'falhou', texto: r.mensagem || MENSAGEM_FALHA_CEP });
      })
      .catch((e) => {
        terminou = true;
        if (vigente) setSituacao({ tipo: 'falhou', texto: e instanceof ErroApi && e.status === 429 ? e.message : MENSAGEM_FALHA_CEP });
      });
    return () => {
      vigente = false;
      // CEP trocado antes da resposta: a consulta descartada não conta como "já consultado"
      if (!terminou && ultimo.current === d) ultimo.current = '';
    };
  }, [cep]);
  // CEP que já veio preenchido por outra fonte (ex.: consulta de CNPJ): não consulta de novo nem sobrescreve o endereço
  const marcarConsultado = (c: string) => {
    ultimo.current = soDigitos(c);
    setSituacao(null);
  };
  return { situacao, marcarConsultado };
}

/** Dados cadastrais sugeridos pela consulta de CNPJ (contrato DadosCnpj do backend; a IE não vem da fonte). */
export interface DadosCnpjTela {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  situacaoCadastral: 'ativa' | 'suspensa' | 'inapta' | 'baixada' | 'nula' | null;
  matriz: boolean | null;
  endereco: { cep: string | null; logradouro: string | null; numero: string | null; complemento: string | null; bairro: string | null; cidade: string | null; uf: string | null };
}
type RespostaCnpj = { encontrado: true; dados: DadosCnpjTela; nomeFonte: string; consultadoEm: string; alerta: string | null } | { encontrado: false; motivo: string; mensagem: string };
export type SituacaoConsultaCnpj = { tipo: 'consultando' | 'encontrado' | 'falhou'; texto: string; alerta?: string | null } | null;
const MENSAGEM_FALHA_CNPJ = 'A consulta do CNPJ está temporariamente indisponível. Preencha os dados manualmente e tente novamente mais tarde.';

/**
 * Consulta AUTOMÁTICA de CNPJ na Receita (BrasilAPI; decisão da Valéria, 07/10/2026), no mesmo padrão do CEP: só na
 * INCLUSÃO da entidade, quando o CNPJ fica completo e VÁLIDO (dígitos verificadores; a faixa fictícia de demonstração
 * não sai para a rede) e diferente do último consultado. O resultado é sugestão editável, com a fonte, a hora e o
 * alerta da situação cadastral (nenhuma situação bloqueia); a falha só informa. Nada é gravado pela consulta.
 */
export function useConsultaCnpj(cnpj: string, ativo: boolean, aoEncontrar: (d: DadosCnpjTela) => void): SituacaoConsultaCnpj {
  const ultimo = useRef('');
  const callback = useRef(aoEncontrar);
  callback.current = aoEncontrar;
  const [situacao, setSituacao] = useState<SituacaoConsultaCnpj>(null);
  useEffect(() => {
    if (!ativo) return;
    const d = soDigitos(cnpj);
    if (d.length !== 14 || !validarCnpj(cnpj).ok) {
      if (d !== ultimo.current) setSituacao(null);
      return;
    }
    if (d === ultimo.current) return;
    ultimo.current = d;
    let vigente = true;
    let terminou = false;
    setSituacao({ tipo: 'consultando', texto: 'Consultando o CNPJ na Receita Federal…' });
    api
      .get<RespostaCnpj>(`/api/clientes/consulta-cnpj/${d}`)
      .then((r) => {
        terminou = true;
        if (!vigente) return;
        if (r.encontrado) {
          callback.current(r.dados);
          const quando = new Date(r.consultadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
          setSituacao({ tipo: 'encontrado', texto: `Dados consultados via ${r.nomeFonte} em ${quando} — confira antes de continuar. A inscrição estadual é informada manualmente.`, alerta: r.alerta });
        } else setSituacao({ tipo: 'falhou', texto: r.mensagem || MENSAGEM_FALHA_CNPJ });
      })
      .catch((e) => {
        terminou = true;
        if (vigente) setSituacao({ tipo: 'falhou', texto: e instanceof ErroApi && e.status === 429 ? e.message : MENSAGEM_FALHA_CNPJ });
      });
    return () => {
      vigente = false;
      if (!terminou && ultimo.current === d) ultimo.current = '';
    };
  }, [cnpj, ativo]);
  return situacao;
}

export function SeletorUf({ valor, aoMudar, teste, rotulo = 'UF' }: { valor: string; aoMudar: (v: string) => void; teste?: string; rotulo?: string }) {
  return (
    <label>
      {rotulo}
      <select value={valor} onChange={(e) => aoMudar(e.target.value)} data-teste={teste}>
        <option value="">Selecione…</option>
        {UFS.map((u) => (
          <option key={u.sigla} value={u.sigla}>
            {u.sigla} — {u.nome}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Opções de "Quem indicou?": Clientes, contatos e parceiros comerciais (ou nome livre, se ainda não cadastrado). */
function useOpcoesIndicacao(busca: string, ignorarClienteId?: string): Indicacao[] {
  const [opcoes, setOpcoes] = useState<Indicacao[]>([]);
  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) return setOpcoes([]);
    let vivo = true;
    const t = window.setTimeout(async () => {
      const qs = `?busca=${encodeURIComponent(q)}`;
      const [cls, ks, ps] = await Promise.all([
        api.get<ClienteResumo[]>(`/api/clientes${qs}`).catch(() => []),
        api.get<{ contatos: ContatoConsolidado[] }>(`/api/contatos${qs}`).catch(() => ({ contatos: [] })),
        api.get<{ id: string; nome: string }[]>(`/api/parceiros${qs}`).catch(() => []),
      ]);
      if (!vivo) return;
      setOpcoes([
        ...cls.filter((c) => c.id !== ignorarClienteId).map((c): Indicacao => ({ tipo: 'cliente', id: c.id, nome: c.nome })),
        ...ks.contatos.map((k): Indicacao => ({ tipo: 'contato', id: k.id, nome: `${k.nome} (${k.cliente.nome})` })),
        ...ps.map((p): Indicacao => ({ tipo: 'parceiro', id: p.id, nome: p.nome })),
      ]);
    }, 250);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [busca, ignorarClienteId]);
  return opcoes;
}

/**
 * Origem do Cliente. `atual` (edição): a Origem gravada continua disponível mesmo fora da lista (ex.: "Não informado
 * na origem", dos clientes migrados) — editar outros campos não obriga a trocá-la; uma Origem NOVA vem da lista.
 */
export function CampoOrigem({ origens, origem, setOrigem, indicacao, setIndicacao, teste = 'origem', ignorarClienteId, atual }: { origens: string[]; origem: string; setOrigem: (o: string) => void; indicacao?: Indicacao | null; setIndicacao: (i?: Indicacao) => void; teste?: string; ignorarClienteId?: string; atual?: string }) {
  const [busca, setBusca] = useState('');
  const opcoes = useOpcoesIndicacao(busca, ignorarClienteId);
  return (
    <>
      <label>
        Origem
        <select value={origem} onChange={(e) => (setOrigem(e.target.value), e.target.value !== 'Indicação' && setIndicacao(undefined))} data-teste={teste}>
          <option value="">Selecione…</option>
          {atual && !origens.includes(atual) && (
            <option value={atual} data-teste="origem-atual-fora-da-lista">
              {atual} (valor atual)
            </option>
          )}
          {origens.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </label>
      {origem === 'Indicação' && (
        <div className="campo-indicacao coluna-dupla" data-teste="quem-indicou">
          <label>
            Quem indicou?
            <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Busque Cliente, contato ou parceiro comercial" data-teste="busca-indicacao" />
          </label>
          {indicacao ? (
            <p className="indicacao-escolhida">
              Indicado por <strong>{textoIndicacao(indicacao)}</strong>{' '}
              <button type="button" className="botao-link" onClick={() => setIndicacao(undefined)}>
                Trocar
              </button>
            </p>
          ) : (
            busca.trim().length >= 2 && (
              <ul className="opcoes-indicacao" role="listbox" aria-label="Resultados de quem indicou">
                {opcoes.slice(0, 8).map((o) => (
                  <li key={`${o.tipo}-${o.id}`}>
                    <button type="button" className="botao-link" role="option" aria-selected={false} onClick={() => (setIndicacao(o), setBusca(''))}>
                      {o.nome} <small className="texto-secundario">({rotuloTipoIndicacao(o.tipo)})</small>
                    </button>
                  </li>
                ))}
                <li>
                  <button type="button" className="botao-link" role="option" aria-selected={false} onClick={() => (setIndicacao({ tipo: 'livre', nome: busca.trim() }), setBusca(''))} data-teste="indicacao-livre">
                    Usar “{busca.trim()}” como nome livre (ainda não cadastrado)
                  </button>
                </li>
              </ul>
            )
          )}
        </div>
      )}
    </>
  );
}
